import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { OrganizationNode } from "../../redux/organization";
import { pathToNodeSelectorCreator } from "../../redux/organization/selectors";
import { IAppState } from "../../redux/types";
import { RoleType } from "../../redux/userProfile";
import { FLAGS } from "../featureFlags/FeatureFlags";

export const useStateRef = (defaultValue: any) => {
  const [state, setState] = useState(defaultValue);
  const ref = useRef(defaultValue);
  ref.current = state;
  return [
    state,
    function (newValue: any) {
      ref.current = newValue;
      return setState(newValue);
    },
    ref,
  ];
};
export const useWindowEvent = (event, callback) => {
  useEffect(() => {
    window.addEventListener(event, callback, true);
    return () => window.removeEventListener(event, callback);
  }, [event, callback]);
};
export const useClickNotOnMe = (callBack, ref) => {
  const wrapper = useCallback(
    (event) => {
      if (ref.current && !ref.current.contains(event.target)) callBack(event);
    },
    [callBack, ref]
  );

  return useWindowEvent("mousedown", wrapper);
};

export const usePrevious = (value: any): any => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef(undefined);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

export const useIsAllowedCallback = (
  todoCallback: () => void,
  deps: any[],
  checks: boolean[] = [true]
) => {
  const { roleName } = useSelector((state: IAppState) => state.userProfile);
  const { socketsWorking } = useSelector(
    (state: IAppState) => state.healthCheck
  );
  const wrapped = useCallback(() => {
    if (
      socketsWorking &&
      roleName !== RoleType.VIEWER &&
      checks?.every((el) => el)
    )
      todoCallback();
  }, [todoCallback, socketsWorking, checks, roleName]);
  return wrapped;
};

export const useIsAllowedForViewerCallback = (
  todoCallback: () => void,
  deps: any[],
  checks: boolean[] = [true]
) => {
  const { socketsWorking } = useSelector(
    (state: IAppState) => state.healthCheck
  );
  const wrapped = useCallback(() => {
    if (socketsWorking && checks?.every((el) => el)) todoCallback();
  }, [todoCallback, socketsWorking, checks]);
  return wrapped;
};

export const useFeatureFlag = (flag: FLAGS) => {
  const featureVariable = useSelector(
    (state: IAppState) => state.healthCheck.featureFlags[flag]
  );
  const featurePrev = usePrevious(featureVariable);
  const [, setneedUpdate, needUpdateRef] = useStateRef(Math.random());
  useEffect(() => {
    if (featureVariable !== featurePrev) {
      setneedUpdate(Math.random());
    }
  }, [featureVariable, featurePrev, setneedUpdate]);
  return { featureVariable, featurePrev, needUpdate: needUpdateRef.current };
};

export const useMemoCompare = (next, compare) => {
  // Ref for storing previous value
  const previousRef = useRef(undefined);
  const previous = previousRef.current;
  // Pass previous and next value to compare function
  // to determine whether to consider them equal.
  const isEqual = compare(previous, next);
  // If not equal update previousRef to next value.
  // We only update if not equal so that this hook continues to return
  // the same old value if compare keeps returning true.
  useEffect(() => {
    if (!isEqual) {
      previousRef.current = next;
    }
  });
  // Finally, if equal then return the previous value
  return isEqual ? previous : next;
};

export const useAlertTimer = (date: string, onlyMinutes = false): string => {
  const getTimeDiff = useCallback(
    (lastUpdate: string): string => {
      const now = moment();
      const local = moment.utc(lastUpdate).local();
      if (now < local) return "00:00:00";
      const diffTime = moment(now).diff(local);

      const duration = moment.duration(diffTime, "milliseconds");
      const fromHours = Math.floor(duration.asHours());
      const fromMinutes = Math.floor(duration.asMinutes() - fromHours * 60);
      const fromSeconds = Math.floor(
        duration.asSeconds() - fromMinutes * 60 - fromHours * 60 * 60
      );

      return !onlyMinutes
        ? ("0" + fromHours).slice(-2) +
            ":" +
            ("0" + fromMinutes).slice(-2) +
            ":" +
            ("0" + fromSeconds).slice(-2)
        : ("0" + fromMinutes).slice(-2) + ":" + ("0" + fromSeconds).slice(-2);
    },
    [onlyMinutes]
  );

  const [waitStr, setWaitStr] = useState(getTimeDiff(date));

  useEffect(() => {
    let interval: any;

    if (date?.length > 0) {
      interval = setInterval(() => {
        setWaitStr(getTimeDiff(date));
      }, 1000);
    }
    return () => {
      clearInterval(interval);
    };
  }, [date, getTimeDiff]);

  return waitStr;
};

export const usePathToNode = (
  nodeId: number,
  includeRoot = false
): OrganizationNode[] => {
  const selection = useMemo(() => pathToNodeSelectorCreator(), []);
  const nodePath = useSelector((state: IAppState) =>
    selection(state, nodeId, includeRoot)
  );
  return nodePath;
};
